import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useBrandsQuery } from './brandsApi';
import { messageOf } from './lib';
import BrandFormDialog from './components/BrandFormDialog';

/**
 * ADR-0023 §19 — the list of brands this tenant groups its own branches
 * under. `brands.view` gates the route (`Routes.tsx`); `brands.manage` gates
 * the one mutating control here (Create) — deciding which branches count as
 * "this franchisor's own" is a tenant-admin, boundary-DEFINING act, not
 * something a franchisor grants itself (see `BrandFormDialog`'s own note).
 */
export default function Brands() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canManage = can('brands.manage');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  // Same 250ms debounce `Pages/Billing/Wallet.tsx` uses for its own search —
  // otherwise every keystroke is a fresh request rather than the one someone
  // finished typing.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useBrandsQuery({ search: debouncedSearch || undefined, limit: 50 });
  const items = query.data?.items ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('brands.title')}</h1>
          <p className="text-sm text-ink-muted">{t('brands.subtitle')}</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('brands.create')}
          </Button>
        )}
      </div>

      <div className="max-w-xs">
        <Field label={t('brands.search')} controlId="brands-search">
          <Input
            id="brands-search"
            placeholder={t('brands.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
      </div>

      {query.isLoading ? (
        <LoadingState />
      ) : query.error ? (
        // A brand list that failed to load must never look like a tenant with no
        // brands — this is its own state, with its own retry, never the empty one.
        <ErrorState
          message={messageOf(query.error, t('brands.loadFailed'))}
          onRetry={() => query.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={debouncedSearch ? t('brands.emptyFiltered') : t('brands.empty')}
          hint={debouncedSearch ? t('brands.emptyFilteredHint') : t('brands.emptyHint')}
          action={
            !debouncedSearch && canManage ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                {t('brands.create')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('brands.columnName')}</TH>
                <TH>{t('brands.columnSlug')}</TH>
                <TH className="text-end tabular-nums">{t('brands.columnBranches')}</TH>
              </TR>
            </THead>
            <tbody>
              {items.map((brand) => (
                <TR
                  key={brand.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/brands/${brand.id}/overview`)}
                >
                  <TD className="font-medium">{brand.name}</TD>
                  <TD className="text-ink-muted">{brand.slug}</TD>
                  <TD className="text-end tabular-nums">{brand._count.branches}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {createOpen && (
        <BrandFormDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => navigate(`/brands/${id}/overview`)}
        />
      )}
    </div>
  );
}
